/* Dreams by Rusatsi — sitewide ambient light layer.
   Deterministic, seeded points of light, placed only inside genuinely empty
   background space -- refined and slow, never a dense or literal star field.
   The homepage uses a higher-density table (data-dbr-ambient-density="high")
   than product/Preview Collection pages, which keep the original, more
   restrained counts. Exclusion zones are measured from real DOM elements
   (headings, paragraphs, links, buttons, forms, imagery, nav, header) at
   runtime, with a safety buffer around each -- a candidate position is
   rejected outright (never just dimmed) when it falls inside any of them.
   Configuration (max light count per breakpoint, colour bias) is read from
   the [data-dbr-ambient-lights] element itself, set per-template in
   layout/theme.liquid. */
(function () {
  'use strict';

  var root = document.querySelector('[data-dbr-ambient-lights]');
  if (!root) return;

  var MAX_PLACEMENT_ATTEMPTS = 14;

  var COLOR_TEMPLATES = [
    'rgba(243, 237, 228, OPACITY)', /* warm ivory */
    'rgba(168, 121, 61, OPACITY)',  /* muted bronze */
    'rgba(165, 181, 202, OPACITY)'  /* rare blue-grey */
  ];
  var COLOR_WEIGHTS = [0.64, 0.23, 0.13];

  /* Desktop >=1200, tablet 768-1199, mobile <768 -- matches the breakpoints
     already used throughout this theme's own CSS (dbr-home-curved-gallery,
     dbr-about-artist, etc). Counts and buffers per PART 7 of the audit spec. */
  var BREAKPOINTS = [
    { minWidth: 1200, min: 8, max: 14, textBuffer: 54, mediaBuffer: 74 },
    { minWidth: 768, min: 6, max: 10, textBuffer: 44, mediaBuffer: 60 },
    { minWidth: 0, min: 4, max: 7, textBuffer: 28, mediaBuffer: 44 }
  ];

  /* Homepage-only, higher-density table (Pass 8C) -- selected when the root
     element carries data-dbr-ambient-density="high" (set only for the index
     template in layout/theme.liquid). Product and Preview Collection pages
     keep the original counts above untouched. Ranges target ~70-110 visible
     points on desktop and ~30-50 on mobile, per the homepage star-visibility
     spec -- a visual target, not a literal per-viewport count, since points
     are still distributed across the full page height. */
  var HOME_BREAKPOINTS = [
    { minWidth: 1200, min: 78, max: 104, textBuffer: 54, mediaBuffer: 74 },
    { minWidth: 768, min: 54, max: 76, textBuffer: 44, mediaBuffer: 60 },
    { minWidth: 0, min: 32, max: 48, textBuffer: 28, mediaBuffer: 44 }
  ];

  var EXCLUSION_SELECTOR = [
    'img', 'picture', 'video', 'canvas', 'svg:not([aria-hidden="true"])',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'blockquote',
    'a', 'button', 'input', 'textarea', 'select', 'form', 'label',
    'nav', 'header', 'footer',
    '[role="button"]', '[role="link"]', '[data-artwork-link]'
  ].join(',');

  var MEDIA_SELECTOR = 'img,picture,video,canvas,figure';

  function mulberry32(seed) {
    return function () {
      seed |= 0;
      seed = (seed + 0x6d2b79f5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /* Deterministic string seed (page template + handle) so recalculation on
     resize/section-reload does not cause the composition to jump randomly. */
  function seedFromString(str) {
    var h = 0;
    for (var i = 0; i < str.length; i++) {
      h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
    }
    return h || 1;
  }

  function pickColorTemplate(rand) {
    var r = rand();
    var acc = 0;
    for (var i = 0; i < COLOR_WEIGHTS.length; i++) {
      acc += COLOR_WEIGHTS[i];
      if (r <= acc) return COLOR_TEMPLATES[i];
    }
    return COLOR_TEMPLATES[0];
  }

  function withOpacity(template, opacity) {
    return template.replace('OPACITY', opacity.toFixed(2));
  }

  function breakpointFor(width, isHomeDensity) {
    var table = isHomeDensity ? HOME_BREAKPOINTS : BREAKPOINTS;
    for (var i = 0; i < table.length; i++) {
      if (width >= table[i].minWidth) return table[i];
    }
    return table[table.length - 1];
  }

  function collectExclusionRects(main, mainRect, bp) {
    var rects = [];
    var els = main.querySelectorAll(EXCLUSION_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) continue;
      var isMedia = el.matches(MEDIA_SELECTOR);
      var buffer = isMedia ? bp.mediaBuffer : bp.textBuffer;
      rects.push({
        left: r.left - mainRect.left - buffer,
        right: r.left - mainRect.left + r.width + buffer,
        top: r.top - mainRect.top - buffer,
        bottom: r.top - mainRect.top + r.height + buffer
      });
    }
    /* The fixed header can sit above the very top of the page at rest --
       protect that opening strip outright regardless of its translucency. */
    var header = document.querySelector('[data-dbr-header]');
    if (header) {
      var hr = header.getBoundingClientRect();
      rects.push({
        left: -Infinity,
        right: Infinity,
        top: -Infinity,
        bottom: hr.bottom - mainRect.top + bp.textBuffer
      });
    }
    return rects;
  }

  function insideAnyRect(x, y, rects) {
    for (var i = 0; i < rects.length; i++) {
      var r = rects[i];
      if (x > r.left && x < r.right && y > r.top && y < r.bottom) return true;
    }
    return false;
  }

  function build(main) {
    var previous = main.querySelector(':scope > [data-dbr-ambient-lights-layer]');
    if (previous) previous.parentNode.removeChild(previous);

    var mainRect = main.getBoundingClientRect();
    var totalWidth = mainRect.width;
    var totalHeight = Math.max(main.scrollHeight, mainRect.height);
    if (!totalWidth || !totalHeight) return null;

    var isHomeDensity = root.getAttribute('data-dbr-ambient-density') === 'high';
    var bp = breakpointFor(window.innerWidth, isHomeDensity);
    var exclusionRects = collectExclusionRects(main, mainRect, bp);

    var seedKey = (root.getAttribute('data-dbr-ambient-seed') || 'dbr') + '-' + bp.minWidth;
    var rand = mulberry32(seedFromString(seedKey));

    var targetCount = bp.min + Math.floor(rand() * (bp.max - bp.min + 1));

    var layer = document.createElement('div');
    layer.className = 'dbr-ambient-lights';
    layer.setAttribute('data-dbr-ambient-lights-layer', '');
    layer.setAttribute('aria-hidden', 'true');

    var placed = 0;
    for (var i = 0; i < targetCount; i++) {
      var x = null, y = null, attempts = 0, found = false;
      while (attempts < MAX_PLACEMENT_ATTEMPTS) {
        var cx = rand() * totalWidth;
        var cy = rand() * totalHeight;
        attempts++;
        if (!insideAnyRect(cx, cy, exclusionRects)) {
          x = cx;
          y = cy;
          found = true;
          break;
        }
      }
      /* No safe candidate found within the attempt budget for this point --
         skip it rather than force a placement on top of protected content.
         A crowded page therefore ends up with fewer lights, never more
         overlap. */
      if (!found) continue;

      /* Bright/large is a small minority everywhere. On the homepage
         (isHomeDensity) both size and opacity run visibly higher across the
         board, per the Pass 8C star-visibility target -- product/Preview
         Collection keep the original, more restrained values. */
      var isSoft = rand() < 0.18;
      var size = isSoft ? 1.5 + rand() * 0.5 : 1 + rand() * 0.4;
      var opacity;
      if (isHomeDensity) {
        opacity = isSoft ? 0.36 + rand() * 0.2 : 0.24 + rand() * 0.18;
      } else {
        opacity = isSoft ? 0.24 + rand() * 0.14 : 0.16 + rand() * 0.14;
      }
      var colorTemplate = pickColorTemplate(rand);

      /* Motion: most stars are fully static. Only a small minority (10-15%)
         get a slow, opacity-only twinkle -- no position movement for any
         star, on the homepage or elsewhere. */
      var isTwinkling = rand() < 0.12;
      var breatheDuration = 6 + rand() * 8; /* 6-14s */
      var delay = -1 * rand() * breatheDuration;

      var light = document.createElement('span');
      light.className = 'dbr-ambient-light' + (isTwinkling ? ' is-twinkling' : '');
      light.style.left = x.toFixed(1) + 'px';
      light.style.top = y.toFixed(1) + 'px';
      light.style.setProperty('--light-size', size.toFixed(2) + 'px');
      light.style.setProperty('--light-colour', withOpacity(colorTemplate, opacity));
      light.style.setProperty('--light-opacity', opacity.toFixed(2));
      if (isTwinkling) {
        light.style.setProperty('--light-breathe-duration', breatheDuration.toFixed(1) + 's');
        light.style.setProperty('--light-delay', delay.toFixed(2) + 's');
      }

      layer.appendChild(light);
      placed++;
    }

    if (!placed) return null;

    main.insertBefore(layer, main.firstChild);
    return layer;
  }

  function rebuild() {
    var main = document.querySelector('main#MainContent');
    if (main) build(main);
  }

  function init() {
    var main = document.querySelector('main#MainContent');
    if (!main) return;
    build(main);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(rebuild);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Rebuild once more after images settle final layout heights, so light
     positions and exclusion zones match the real rendered page. */
  window.addEventListener('load', rebuild);

  /* Debounced resize -- recalculates safe placement on breakpoint/geometry
     changes without re-running on every intermediate resize frame. */
  var resizeTimer = null;
  window.addEventListener(
    'resize',
    function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(rebuild, 200);
    },
    { passive: true }
  );

  /* Shopify Theme Editor: a section add/reorder/remove can change content
     height or position, so rebuild rather than leaving stale positions.
     Idempotent -- build() always removes the previous layer first. */
  document.addEventListener('shopify:section:load', rebuild);
  document.addEventListener('shopify:section:unload', rebuild);
})();
